/**
 * COLLECTION FILTERS FUNCTIONALITY
 * Handles product filtering, sorting, and mobile modal interactions
 */

class CollectionFilters {
  constructor() {
    this.productGrid = document.getElementById('product-grid');
    this.productItems = document.querySelectorAll('.product-item');
    this.productCount = document.getElementById('product-count');
    this.loadingOverlay = document.getElementById('loading-overlay');
    this.noProductsMessage = document.getElementById('no-products-message');
    this.promotionalCard = document.querySelector('.promotional-card-item');
    
    // iOS Detection
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Ensure loading overlay is initially hidden
    this.ensureLoadingOverlayHidden();
    
    this.activeFilters = {
      sort: 'manual',
      productTypes: [],
      sizes: [],
      gender: '',
      colors: [],
      mafiaTag: ''
    };
    
    // Track hidden filters that shouldn't appear in active filters display
    this.hiddenProductTypeFilter = null;
    
    this.init();
  }

  /**
   * Initialize all event listeners and functionality
   */
  init() {
    // iOS-specific initialization
    if (this.isIOS) {
      this.initIOSSpecificHandling();
    }
    
    this.initDesktopFilters();
    this.initMobileModal();
    this.initActiveFiltersDisplay();
    this.initQuickAddButtons();
    this.initCollectionToggle();
    this.initCollectionDropdowns();
    
    // Check for URL parameters and apply filters automatically
    this.checkURLFilters();
    
    // Apply initial filtering for product type collections even without explicit filters
    this.applyInitialFiltering();
    
    // Additional mobile compatibility check
    setTimeout(() => {
      this.ensureMobileCompatibility();
    }, 300);
    
    // Debug: Check filter state after initialization
    setTimeout(() => {
      this.debugFilterState();
    }, 500);
  }

  /**
   * Ensure mobile compatibility for filter functionality
   */
  ensureMobileCompatibility() {
    const currentPath = window.location.pathname;
    const productTypeCollections = ['/collections/hoodies', '/collections/tanks', '/collections/tees'];
    const isOnProductTypeCollection = productTypeCollections.includes(currentPath);
    
    // Check for gender parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const genderParam = urlParams.get('gender');
    
    if (genderParam) {
      const validGenders = ['men', 'women'];
      const gender = genderParam.toLowerCase();
      
      if (validGenders.includes(gender)) {
        // Apply gender filter immediately
        this.activeFilters.gender = gender;
        this.activateGenderButton(gender);
      }
    }
    
    if (isOnProductTypeCollection) {
      // Ensure collection toggle is properly activated
      this.activateLakeLifeMafiaCollection();
      
      // Re-apply filters to ensure everything is working
      this.applyFiltersImmediate();
      
      // Force mobile button states
      const mobileButtons = document.querySelectorAll('.mobile-product-type');
      mobileButtons.forEach(button => {
        const productType = button.dataset.productType;
        if (productType && this.activeFilters.productTypes.includes(productType)) {
          button.classList.add('selected');
        }
      });
      
      // Force mobile gender button states
      const mobileGenderButtons = document.querySelectorAll('.mobile-gender-option');
      mobileGenderButtons.forEach(button => {
        const gender = button.dataset.gender;
        if (gender && this.activeFilters.gender === gender) {
          button.classList.add('selected');
        }
      });
      
      // Ensure mobile modal filters are properly initialized
      const mobileModal = document.getElementById('mobile-filters-modal');
      if (mobileModal) {
        // Force re-initialization of mobile filters
        this.initMobileFilters(mobileModal);
      }
    }
  }

  /**
   * iOS-specific handling to prevent white screen and touch issues
   */
  initIOSSpecificHandling() {
    // Prevent iOS touch callouts and selections that can interfere
    document.body.style.webkitTouchCallout = 'none';
    document.body.style.webkitUserSelect = 'none';
    
    // Force immediate visibility of products on iOS
    setTimeout(() => {
      this.productItems.forEach(item => {
        if (item.style.display === 'none' && !item.hasAttribute('data-filtered')) {
          item.style.display = '';
        }
      });
      
      // Ensure loading overlays are hidden
      this.forceHideAllLoadingOverlays();
    }, 50);

    // iOS-specific page visibility handling
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Page became visible, ensure proper state
        this.forceHideAllLoadingOverlays();
      }
    });
  }

  /**
   * Get Lake Life Mafia collection path from toggle
   */
  getLakeLifeMafiaPath() {
    const collectionToggle = document.getElementById('collection-toggle');
    if (!collectionToggle) return '/collections/lake-life-mafia-collection'; // fallback
    
    const firstToggleOption = collectionToggle.querySelector('.collection-toggle__option');
    if (!firstToggleOption) return '/collections/lake-life-mafia-collection'; // fallback
    
    const href = firstToggleOption.getAttribute('href');
    if (!href) return '/collections/lake-life-mafia-collection'; // fallback
    
    return href;
  }

  /**
   * Check URL for automatic filter application
   */
  checkURLFilters() {
    // First check URL parameters (for existing functionality)
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');
    const genderParam = urlParams.get('gender');
    
    // Check for gender parameter from menu navigation (SHOP BY -> MEN/WOMEN)
    if (genderParam) {
      const validGenders = ['men', 'women'];
      const gender = genderParam.toLowerCase();
      
      if (validGenders.includes(gender)) {
        // Activate Lake Life Mafia Collection toggle for product type pages
        this.activateLakeLifeMafiaCollection();
        
        // Apply gender filter
        this.activeFilters.gender = gender;
        
        // Activate gender button visually
        this.activateGenderButton(gender);
        
        // Apply filters immediately (no delay for URL-based filtering)
        this.applyFiltersImmediate();
        
        // Force mobile compatibility - ensure filters are applied on mobile too
        setTimeout(() => {
          this.forceMobileFilterApplication();
        }, 100);
        
        return;
      }
    }
    
    if (filterParam) {
      this.activateLakeLifeMafiaCollection();
      this.applyURLFilter(filterParam);
      return;
    }
    
    // Check if we're on a specific product type collection page
    const currentPath = window.location.pathname;
    
    // Define the product type collections that should have Lake Life Mafia toggle
    const productTypeCollections = [
      '/collections/hoodies',
      '/collections/tanks', 
      '/collections/tees'
    ];
    
    // Check if current path matches any product type collection
    const isProductTypeCollection = productTypeCollections.includes(currentPath);
    
    if (isProductTypeCollection && this.isCollectionToggleEnabled()) {
      // Activate Lake Life Mafia Collection toggle for product type pages
      this.activateLakeLifeMafiaCollection();
      
      // Extract product type from URL and apply filter silently (no visual indication)
      const productType = currentPath.split('/collections/')[1];
      if (productType && ['hoodies', 'tanks', 'tees'].includes(productType)) {
        this.activeFilters.productTypes = [productType];
        // Don't show visual button - this is implicit for this collection
        // this.activateProductTypeButton(productType); - REMOVED
        this.applyFiltersImmediate();
        
        // Mark this as a hidden filter for this collection
        this.hiddenProductTypeFilter = productType;
        
        // Force mobile compatibility - ensure filters are applied on mobile too
        setTimeout(() => {
          this.forceMobileFilterApplication();
        }, 100);
      }
    }
  }

  /**
   * Force mobile filter application to ensure compatibility
   */
  forceMobileFilterApplication() {
    const currentPath = window.location.pathname;
    const productTypeCollections = ['/collections/hoodies', '/collections/tanks', '/collections/tees'];
    const isOnProductTypeCollection = productTypeCollections.includes(currentPath);
    
    if (isOnProductTypeCollection) {
      // Re-apply filters to ensure mobile compatibility
      this.applyFiltersImmediate();
      
      // Force refresh of mobile elements
      const mobileButtons = document.querySelectorAll('.mobile-product-type');
      mobileButtons.forEach(button => {
        const productType = button.dataset.productType;
        if (productType && this.activeFilters.productTypes.includes(productType)) {
          button.classList.add('selected');
        } else {
          button.classList.remove('selected');
        }
      });
      
      // Force refresh of mobile gender buttons
      const mobileGenderButtons = document.querySelectorAll('.mobile-gender-option');
      mobileGenderButtons.forEach(button => {
        const gender = button.dataset.gender;
        if (gender && this.activeFilters.gender === gender) {
          button.classList.add('selected');
        } else {
          button.classList.remove('selected');
        }
      });
      
      // Ensure collection toggle is active on mobile
      this.activateLakeLifeMafiaCollection();
    }
  }

  /**
   * Activate Lake Life Mafia Collection toggle
   */
  activateLakeLifeMafiaCollection() {
    const collectionToggle = document.getElementById('collection-toggle');
    if (!collectionToggle) return;
    
    const toggleOptions = collectionToggle.querySelectorAll('.collection-toggle__option');
    if (toggleOptions.length < 2) return;
    
    // Remove active from all options
    toggleOptions.forEach(option => option.classList.remove('active'));
    
    // Activate the first option (Lake Life Mafia Collection)
    toggleOptions[0].classList.add('active');
    
    // Hide the mafia filter since we're on Lake Life Mafia Collection
    const desktopMafiaFilter = document.getElementById('desktop-choose-mafia-filter');
    const mobileMafiaFilter = document.getElementById('mobile-choose-mafia-filter');
    
    if (desktopMafiaFilter) desktopMafiaFilter.style.display = 'none';
    if (mobileMafiaFilter) mobileMafiaFilter.style.display = 'none';
  }

  /**
   * Apply filter based on URL parameter
   */
  applyURLFilter(filterType) {
    const validFilters = ['tees', 'tanks', 'hoodies', 'decals'];
    const filterKey = filterType.toLowerCase();
    
    if (!validFilters.includes(filterKey)) {
      return;
    }
    
    // Add to active filters
    this.activeFilters.productTypes = [filterKey];
    
    // Activate the visual elements
    this.activateProductTypeButton(filterKey);
    
    // Apply the filters immediately (no delay for URL-based filtering)
    this.applyFiltersImmediate();
  }

  /**
   * Apply filters immediately without delay (for URL-based filtering)
   */
  applyFiltersImmediate() {
    let visibleCount = 0;
    
    this.productItems.forEach(item => {
      let isVisible = true;
      
      // Check if we're on a product type collection page (tees, tanks, hoodies)
      const currentPath = window.location.pathname;
      const productTypeCollections = ['/collections/hoodies', '/collections/tanks', '/collections/tees'];
      const isOnProductTypeCollection = productTypeCollections.includes(currentPath);
      
      // Product Type Filter - use same logic as performFiltering
      // Only restrict to Lake Life Mafia products when the collection toggle
      // is enabled. When it's turned off, product-type collections behave like
      // normal collections and show all their products.
      if (isOnProductTypeCollection && this.isCollectionToggleEnabled()) {
        const productCollections = item.dataset.productCollections.split(',').map(col => col.trim().toLowerCase());
        const currentProductType = currentPath.split('/collections/')[1]; // Extract 'tees', 'tanks', 'hoodies'
        
        // Product must be in the current product type collection (tees, tanks, hoodies)
        const isInProductTypeCollection = productCollections.includes(currentProductType);
        
        if (isInProductTypeCollection) {
          // Get Lake Life Mafia collection handle
          const lakeLifeMafiaCollection = this.getLakeLifeMafiaPath().split('/collections/')[1] || 'lake-life-mafia-collection';
          
          // Product must also be in Lake Life Mafia collection
          const isInLakeLifeMafia = productCollections.includes(lakeLifeMafiaCollection);
          
          if (!isInLakeLifeMafia) {
            isVisible = false;
          }
        } else {
          // Product is not in the current product type collection
          isVisible = false;
        }
      }
      // For regular filtering when not on product type collection pages
      else if (this.activeFilters.productTypes.length > 0) {
        const productCollections = item.dataset.productCollections.split(',').map(col => col.trim().toLowerCase());
        
        const isInProductTypeCollection = this.activeFilters.productTypes.some(selectedType => {
          const typeKey = selectedType.toLowerCase();
          return productCollections.includes(typeKey);
        });
        
        if (isInProductTypeCollection) {
          const activeCollection = this.getActiveCollection();
          if (activeCollection) {
            const isInActiveCollection = productCollections.includes(activeCollection);
            if (!isInActiveCollection) {
              isVisible = false;
            }
          }
        } else {
          isVisible = false;
        }
      }
      
      // Gender Filter - Apply when gender parameter is detected in URL
      if (this.activeFilters.gender) {
        const productCollections = item.dataset.productCollections.split(',').map(col => col.trim().toLowerCase());
        const selectedGender = this.activeFilters.gender.toLowerCase();
        
        // Check if product is in the selected gender collection
        const genderFound = productCollections.includes(selectedGender);
        
        if (!genderFound) {
          isVisible = false;
        }
      }
      
      // Other filters would go here if needed...
      
      if (isVisible) {
        item.style.display = '';
        visibleCount++;
      } else {
        item.style.display = 'none';
      }
    });
    
    // Handle pagination visibility
    this.handlePaginationVisibility(visibleCount);
    
    // Apply sorting to visible items
    if (this.activeFilters.sort !== 'manual') {
      this.sortProducts();
    }

    this.updateProductCount(visibleCount);
    this.updateActiveFilters();
    
    // Show/hide no products message
    if (this.noProductsMessage) {
      this.noProductsMessage.style.display = visibleCount === 0 ? 'block' : 'none';
    }
  }

  /**
   * Apply initial filtering for product type collections (mobile and desktop)
   */
  applyInitialFiltering() {
    const currentPath = window.location.pathname;
    const productTypeCollections = ['/collections/hoodies', '/collections/tanks', '/collections/tees'];
    const isOnProductTypeCollection = productTypeCollections.includes(currentPath);
    
    // Check for gender parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const genderParam = urlParams.get('gender');
    
    if (genderParam) {
      const validGenders = ['men', 'women'];
      const gender = genderParam.toLowerCase();
      
      if (validGenders.includes(gender)) {
        // Apply gender filter immediately
        this.activeFilters.gender = gender;
        this.activateGenderButton(gender);
      }
    }
    
    if (isOnProductTypeCollection) {
      // Force apply filters even if no explicit filters are set
      this.applyFiltersImmediate();
      
      // Additional mobile compatibility check
      setTimeout(() => {
        this.forceMobileFilterApplication();
      }, 200);
    }
  }

  /**
   * Activate the product type button visually
   */
  activateProductTypeButton(filterType) {
    // Desktop buttons
    const desktopButton = document.querySelector(`.product-type-option[data-product-type="${filterType}"]:not(.mobile-product-type)`);
    if (desktopButton) {
      desktopButton.classList.add('selected');
    }
    
    // Mobile buttons
    const mobileButton = document.querySelector(`.mobile-product-type[data-product-type="${filterType}"]`);
    if (mobileButton) {
      mobileButton.classList.add('selected');
    }
  }

  /**
   * Activate the gender button visually
   */
  activateGenderButton(gender) {
    // Desktop buttons
    const desktopButton = document.querySelector(`.gender-option[data-gender="${gender}"]:not(.mobile-gender-option)`);
    if (desktopButton) {
      desktopButton.classList.add('selected');
    }
    
    // Mobile buttons
    const mobileButton = document.querySelector(`.mobile-gender-option[data-gender="${gender}"]`);
    if (mobileButton) {
      mobileButton.classList.add('selected');
    }
  }

  /**
   * Debug function to check filter state
   */
  debugFilterState() {
    const urlParams = new URLSearchParams(window.location.search);
    const genderParam = urlParams.get('gender');
    
    console.log('🔍 Debug Filter State:');
    console.log('URL Gender Param:', genderParam);
    console.log('Active Filters:', this.activeFilters);
    console.log('Product Items Count:', this.productItems.length);
    
    // Count visible products
    let visibleCount = 0;
    this.productItems.forEach(item => {
      if (item.style.display !== 'none') {
        visibleCount++;
      }
    });
    
    console.log('Visible Products:', visibleCount);
  }

  /**
   * COLLECTION DROPDOWN INITIALIZATION
   */
  initCollectionDropdowns() {
    // Original collection dropdowns (if they exist elsewhere)
    const desktopDropdown = document.getElementById('collection-select');
    const mobileDropdown = document.getElementById('mobile-collection-select');
    
    if (desktopDropdown) {
      desktopDropdown.addEventListener('change', (e) => {
        this.handleCollectionChange(e.target.value);
      });
    }
    
    if (mobileDropdown) {
      mobileDropdown.addEventListener('change', (e) => {
        this.handleCollectionChange(e.target.value);
        // Sync with desktop dropdown
        if (desktopDropdown) {
          desktopDropdown.value = e.target.value;
        }
      });
    }

    // Mafia dropdowns are handled in main-collection-product-grid.liquid
    // to allow direct navigation to specific mafia collections
  }

  /**
   * Handle collection change
   */
  handleCollectionChange(collectionUrl) {
    if (collectionUrl && collectionUrl !== '') {
      // Navigate to the selected collection
      window.location.href = collectionUrl;
    }
  }

  /**
   * DESKTOP FILTERS INITIALIZATION
   */
  initDesktopFilters() {
    // Sort functionality
    const sortRadios = document.querySelectorAll('input[name="sort"]');
    sortRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          this.activeFilters.sort = radio.value;
          this.applyFilters();
        }
      });
    });

    // Size selection
    const sizeOptions = document.querySelectorAll('.size-option:not(.mobile-size-option)');
    sizeOptions.forEach(option => {
      option.addEventListener('click', () => this.handleSizeFilter(option));
    });

    // Gender selection
    const genderOptions = document.querySelectorAll('.gender-option:not(.mobile-gender-option)');
    genderOptions.forEach(option => {
      option.addEventListener('click', () => this.handleGenderFilter(option));
    });

    // Color selection
    const colorOptions = document.querySelectorAll('.color-option:not(.mobile-color-option)');
    colorOptions.forEach(option => {
      option.addEventListener('click', () => this.handleColorFilter(option));
    });

    // Product Type buttons
    const productTypeButtons = document.querySelectorAll('.product-type-option:not(.mobile-product-type)');
    productTypeButtons.forEach(button => {
      button.addEventListener('click', () => this.handleProductTypeFilter(button));
    });
  }

  /**
   * MOBILE MODAL INITIALIZATION
   */
  initMobileModal() {
    const mobileFiltersButton = document.getElementById('mobile-filters-button');
    const mobileFiltersModal = document.getElementById('mobile-filters-modal');
    const mobileFiltersClose = document.getElementById('mobile-filters-close');
    
    if (!mobileFiltersButton || !mobileFiltersModal || !mobileFiltersClose) return;

    // iPhone specific touch handling for the button
    if (this.isIOS) {
      // Add touch styles for iOS
      mobileFiltersButton.style.webkitTouchCallout = 'none';
      mobileFiltersButton.style.webkitUserSelect = 'none';
      mobileFiltersButton.style.userSelect = 'none';
      
      // Touch events for iOS
      mobileFiltersButton.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openMobileModal();
      }, { passive: false });
      
      // Prevent other touch events from interfering
      mobileFiltersButton.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, { passive: false });
      
      mobileFiltersButton.addEventListener('touchmove', (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, { passive: false });
    } else {
      // Regular click event for non-iOS
      mobileFiltersButton.addEventListener('click', () => this.openMobileModal());
    }
    
    // Close modal events (works for both iOS and non-iOS)
    mobileFiltersClose.addEventListener('click', () => this.closeMobileModal());
    
    if (this.isIOS) {
      mobileFiltersClose.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeMobileModal();
      }, { passive: false });
    }
    
    // Close modal when clicking outside
    mobileFiltersModal.addEventListener('click', (e) => {
      if (e.target === mobileFiltersModal) {
        this.closeMobileModal();
      }
    });

    // Mobile filters event delegation
    this.initMobileFilters(mobileFiltersModal);
  }

  /**
   * MOBILE FILTERS EVENT DELEGATION
   */
  initMobileFilters(modal) {
    // Handle clicks on filter options
    modal.addEventListener('click', (e) => {
      if (e.target.classList.contains('mobile-size-option')) {
        this.handleSizeFilter(e.target, true);
      } else if (e.target.classList.contains('mobile-color-option')) {
        this.handleColorFilter(e.target, true);
      } else if (e.target.classList.contains('mobile-gender-option')) {
        this.handleGenderFilter(e.target, true);
      } else if (e.target.classList.contains('mobile-product-type')) {
        this.handleProductTypeFilter(e.target, true);
      }
    });

    // Handle sort radio changes
    modal.addEventListener('change', (e) => {
      if (e.target.name === 'mobile-sort' && e.target.checked) {
        const desktopRadio = document.querySelector(`input[name="sort"][value="${e.target.value}"]`);
        if (desktopRadio) desktopRadio.checked = true;
        this.activeFilters.sort = e.target.value;
        this.applyFilters();
      }
    });
  }

  /**
   * FILTER HANDLERS
   */
  handleSizeFilter(element, isMobile = false) {
    const size = element.dataset.size;
    const counterpart = isMobile 
      ? document.querySelector(`.size-option[data-size="${size}"]:not(.mobile-size-option)`)
      : document.querySelector(`.mobile-size-option[data-size="${size}"]`);
    
    // Toggle selection
    if (element.classList.contains('selected')) {
      element.classList.remove('selected');
      if (counterpart) counterpart.classList.remove('selected');
      this.activeFilters.sizes = this.activeFilters.sizes.filter(s => s !== size);
    } else {
      element.classList.add('selected');
      if (counterpart) counterpart.classList.add('selected');
      if (!this.activeFilters.sizes.includes(size)) {
        this.activeFilters.sizes.push(size);
      }
    }
    this.applyFilters();
  }

  handleGenderFilter(element, isMobile = false) {
    const gender = element.dataset.gender;
    const allGenderOptions = isMobile 
      ? document.querySelectorAll('.mobile-gender-option')
      : document.querySelectorAll('.gender-option:not(.mobile-gender-option)');
    const counterpart = isMobile
      ? document.querySelector(`.gender-option[data-gender="${gender}"]:not(.mobile-gender-option)`)
      : document.querySelector(`.mobile-gender-option[data-gender="${gender}"]`);
    
    // Toggle selection
    if (element.classList.contains('selected')) {
      element.classList.remove('selected');
      if (counterpart) counterpart.classList.remove('selected');
      this.activeFilters.gender = '';
    } else {
      allGenderOptions.forEach(opt => opt.classList.remove('selected'));
      document.querySelectorAll('.gender-option').forEach(opt => opt.classList.remove('selected'));
      element.classList.add('selected');
      if (counterpart) counterpart.classList.add('selected');
      this.activeFilters.gender = gender;
    }
    this.applyFilters();
  }

  handleColorFilter(element, isMobile = false) {
    const color = element.dataset.color;
    const counterpart = isMobile
      ? document.querySelector(`.color-option[data-color="${color}"]:not(.mobile-color-option)`)
      : document.querySelector(`.mobile-color-option[data-color="${color}"]`);
    
    // Toggle selection
    if (element.classList.contains('selected')) {
      element.classList.remove('selected');
      if (counterpart) counterpart.classList.remove('selected');
      this.activeFilters.colors = this.activeFilters.colors.filter(c => c !== color);
    } else {
      element.classList.add('selected');
      if (counterpart) counterpart.classList.add('selected');
      if (!this.activeFilters.colors.includes(color)) {
        this.activeFilters.colors.push(color);
      }
    }
    this.applyFilters();
  }

  handleProductTypeFilter(element, isMobile = false) {
    const value = element.dataset.productType;
    
    // Special handling for decals - redirect directly to collections/decals
    if (value === 'decals') {
      window.location.href = '/collections/decals';
      return;
    }
    
    const counterpart = isMobile
      ? document.querySelector(`.product-type-option[data-product-type="${value}"]:not(.mobile-product-type)`)
      : document.querySelector(`.mobile-product-type[data-product-type="${value}"]`);
    
    // Prevent double handling
    if (element.hasAttribute('data-processing')) {
      return;
    }
    element.setAttribute('data-processing', 'true');
    
    // Toggle selection
    if (element.classList.contains('selected')) {
      element.classList.remove('selected');
      if (counterpart) counterpart.classList.remove('selected');
      this.activeFilters.productTypes = this.activeFilters.productTypes.filter(type => type !== value);
    } else {
      element.classList.add('selected');
      if (counterpart) counterpart.classList.add('selected');
      if (!this.activeFilters.productTypes.includes(value)) {
        this.activeFilters.productTypes.push(value);
      }
    }
    
    // Remove processing flag immediately
    element.removeAttribute('data-processing');
    
    this.applyFilters();
  }

  /**
   * MOBILE MODAL CONTROLS
   */
  openMobileModal() {
    const modal = document.getElementById('mobile-filters-modal');
    
    // Move modal to end of body to ensure highest stacking context
    if (modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }
    
    // Force the modal to be on top with inline styles
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.right = '0';
    modal.style.bottom = '0';
    modal.style.width = '100vw';
    modal.style.height = '100vh';
    modal.style.zIndex = '2147483647'; // Maximum z-index value
    modal.style.background = 'rgba(0, 0, 0, 0.5)';
    
    // Add modal-open class to body for CSS targeting
    document.body.classList.add('modal-open');
    
    // iPhone specific handling
    if (this.isIOS) {
      // Store current scroll position
      this.scrollPosition = window.scrollY;
      
      // Prevent body scroll on iOS
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
      
      // Force layout calculation to prevent white screen
      modal.style.display = 'block';
      modal.offsetHeight; // Force reflow
      
      // Add active class with a small delay for iOS
      setTimeout(() => {
        modal.classList.add('active');
      }, 10);
    } else {
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
    
    // Ensure mobile filters are properly synchronized
    setTimeout(() => {
      this.syncMobileFilters();
    }, 100);
  }

  /**
   * Sync mobile filters with current state
   */
  syncMobileFilters() {
    const currentPath = window.location.pathname;
    const productTypeCollections = ['/collections/hoodies', '/collections/tanks', '/collections/tees'];
    const isOnProductTypeCollection = productTypeCollections.includes(currentPath);
    
    if (isOnProductTypeCollection) {
      // Sync mobile product type buttons
      const mobileButtons = document.querySelectorAll('.mobile-product-type');
      mobileButtons.forEach(button => {
        const productType = button.dataset.productType;
        if (productType && this.activeFilters.productTypes.includes(productType)) {
          button.classList.add('selected');
        } else {
          button.classList.remove('selected');
        }
      });
      
      // Sync mobile gender buttons
      const mobileGenderButtons = document.querySelectorAll('.mobile-gender-option');
      mobileGenderButtons.forEach(button => {
        const gender = button.dataset.gender;
        if (gender && this.activeFilters.gender === gender) {
          button.classList.add('selected');
        } else {
          button.classList.remove('selected');
        }
      });
      
      // Ensure collection toggle is active
      this.activateLakeLifeMafiaCollection();
    }
  }

  closeMobileModal() {
    const modal = document.getElementById('mobile-filters-modal');
    
    // Remove modal-open class from body
    document.body.classList.remove('modal-open');
    
    if (this.isIOS) {
      // Restore scroll position on iOS
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      // Restore scroll position
      if (typeof this.scrollPosition !== 'undefined') {
        window.scrollTo(0, this.scrollPosition);
      }
      
      modal.classList.remove('active');
      
      // Hide modal after animation on iOS
      setTimeout(() => {
        modal.style.display = 'none';
      }, 300);
    } else {
      modal.classList.remove('active');
      document.body.style.overflow = '';
    }
    
    this.applyFilters();
  }


  /**
   * FILTER APPLICATION
   */
  applyFilters() {
    try {
      // iOS-specific: Add extra delay to prevent race conditions
      if (this.isIOS) {
        setTimeout(() => this.performFiltering(), 10);
      } else {
        this.performFiltering();
      }
    } catch (error) {
      console.error('Error in applyFilters:', error);
      // Ensure loading is hidden even if there's an error
      this.hideLoading();
    }
  }

  performFiltering() {
    try {
      let visibleCount = 0;
      
      this.productItems.forEach(item => {
        let isVisible = true;
        
        // Check if we're on a product type collection page (tees, tanks, hoodies)
        const currentPath = window.location.pathname;
        const productTypeCollections = ['/collections/hoodies', '/collections/tanks', '/collections/tees'];
        const isOnProductTypeCollection = productTypeCollections.includes(currentPath);
        
        // For product type collections (/collections/tees, /collections/tanks, /collections/hoodies)
        // Show only Lake Life Mafia products ONLY while the collection toggle is
        // enabled. With the toggle off, show all products in the collection.
        if (isOnProductTypeCollection && this.isCollectionToggleEnabled()) {
          const productCollections = item.dataset.productCollections.split(',').map(col => col.trim().toLowerCase());
          const currentProductType = currentPath.split('/collections/')[1]; // Extract 'tees', 'tanks', 'hoodies'
          
          // Product must be in the current product type collection (tees, tanks, hoodies)
          const isInProductTypeCollection = productCollections.includes(currentProductType);
          
          if (isInProductTypeCollection) {
            // Get Lake Life Mafia collection handle
            const lakeLifeMafiaCollection = this.getLakeLifeMafiaPath().split('/collections/')[1] || 'lake-life-mafia-collection';
            
            // Product must also be in Lake Life Mafia collection
            const isInLakeLifeMafia = productCollections.includes(lakeLifeMafiaCollection);
            
            if (!isInLakeLifeMafia) {
              isVisible = false;
            }
          } else {
            // Product is not in the current product type collection
            isVisible = false;
          }
        }
        // For regular filtering when not on product type collection pages
        else if (this.activeFilters.productTypes.length > 0) {
          const productCollections = item.dataset.productCollections.split(',').map(col => col.trim().toLowerCase());
          
          const isInProductTypeCollection = this.activeFilters.productTypes.some(selectedType => {
            const typeKey = selectedType.toLowerCase();
            return productCollections.includes(typeKey);
          });
          
          if (isInProductTypeCollection) {
            const activeCollection = this.getActiveCollection();
            if (activeCollection) {
              const isInActiveCollection = productCollections.includes(activeCollection);
              if (!isInActiveCollection) {
                isVisible = false;
              }
            }
          } else {
            isVisible = false;
          }
        }
        
        // Size Filter
        if (this.activeFilters.sizes.length > 0) {
          const productSizes = item.dataset.productSizes ? item.dataset.productSizes.split(',') : [];
          const sizeFound = this.activeFilters.sizes.some(size =>
            productSizes.some(productSize =>
              productSize.trim() === size ||
              productSize.trim().includes(size)
            )
          );
          if (!sizeFound) {
            isVisible = false;
          }
        }
        
        // Gender Filter - Same logic as Product Type Filter
        if (this.activeFilters.gender) {
          const productCollections = item.dataset.productCollections.split(',').map(col => col.trim().toLowerCase());
          const selectedGender = this.activeFilters.gender.toLowerCase();
          
          // Check if product is in the selected gender collection
          const genderFound = productCollections.includes(selectedGender);
          
          if (!genderFound) {
            isVisible = false;
          }
        }
        
        // Color Filter
        if (this.activeFilters.colors.length > 0) {
          const productColors = item.dataset.productColors.split(',');
          const colorFound = this.activeFilters.colors.some(color => 
            productColors.some(productColor => 
              productColor.trim() === color ||
              productColor.trim().includes(color)
            )
          );
          if (!colorFound) {
            isVisible = false;
          }
        }
        
        // Mafia Tag Filter - UNIFIED FLEXIBLE APPROACH
        if (this.activeFilters.mafiaTag) {
          const productTags = item.dataset.productTags.split(',');
          
          let mafiaTagFound = false;
          
          // Define search terms for each mafia filter
          let searchTerms = [];
          
          if (this.activeFilters.mafiaTag === 'lake-cumberland-mafia') {
            searchTerms = ['lake-cumberland', 'lake cumberland mafia', 'lake cumberland', 'cumberland', 'lakecumberland'];
          } else if (this.activeFilters.mafiaTag === 'norris-lake-mafia') {
            searchTerms = ['norris-lake', 'norris lake mafia', 'norris lake', 'norris', 'norrislake'];
          } else if (this.activeFilters.mafiaTag === 'dale-hollow-mafia') {
            searchTerms = ['dale-hollow', 'dale hollow mafia', 'dale hollow', 'dale', 'dalehollow', 'hollow'];
          } else if (this.activeFilters.mafiaTag === 'custom-lake-apparel') {
            searchTerms = ['custom-lake', 'custom lake apparel', 'custom lake', 'custom', 'customlake', 'apparel'];
          }
          
          // Check if any product tag contains any of the search terms
          mafiaTagFound = productTags.some(tag => {
            const cleanTag = tag.trim().toLowerCase();
            return searchTerms.some(term => {
              const searchTerm = term.toLowerCase();
              return cleanTag.includes(searchTerm) || cleanTag === searchTerm;
            });
          });
          
          if (!mafiaTagFound) {
            isVisible = false;
          }
        }
        
        if (isVisible) {
          item.style.display = '';
          visibleCount++;
        } else {
          item.style.display = 'none';
        }
      });
      
      // Hide pagination when filters are active and results fit on one page
      this.handlePaginationVisibility(visibleCount);
      
      // Apply sorting to visible items
      if (this.activeFilters.sort !== 'manual') {
        this.sortProducts();
      }

      // Hide pagination when filters are active and results fit on one page
      this.handlePaginationVisibility(visibleCount);
      
      // Apply sorting to visible items
      if (this.activeFilters.sort !== 'manual') {
        this.sortProducts();
      }

      this.updateProductCount(visibleCount);
      this.updateActiveFilters();
      
      // Show/hide no products message
      if (this.noProductsMessage) {
        this.noProductsMessage.style.display = visibleCount === 0 ? 'block' : 'none';
      }
      
      // Ensure any loading overlays are hidden
      this.hideLoading();
      
      // iOS-specific: Force refresh display state
      if (this.isIOS) {
        this.forceIOSDisplayRefresh();
      }
    } catch (error) {
      console.error('Error in performFiltering:', error);
      // Ensure loading is hidden even if there's an error
      this.hideLoading();
    }
  }

  /**
   * Force display refresh on iOS to prevent white screen issues
   */
  forceIOSDisplayRefresh() {
    // Force a reflow to ensure iOS renders correctly
    const container = this.productGrid;
    if (container) {
      container.style.display = 'none';
      container.offsetHeight; // Trigger reflow
      container.style.display = '';
    }
  }

  /**
   * PRODUCT SORTING
   */
  sortProducts() {
    const visibleItems = Array.from(this.productItems).filter(item => 
      item.style.display !== 'none'
    );
    
    visibleItems.sort((a, b) => {
      switch(this.activeFilters.sort) {
        case 'price-ascending':
          return parseInt(a.dataset.productPrice) - parseInt(b.dataset.productPrice);
        case 'price-descending':
          return parseInt(b.dataset.productPrice) - parseInt(a.dataset.productPrice);
        case 'created-descending':
          return parseInt(b.dataset.productCreated) - parseInt(a.dataset.productCreated);
        case 'title-ascending':
          return a.dataset.productTitle.localeCompare(b.dataset.productTitle);
        default:
          return 0;
      }
    });
    
    // Reorder DOM elements, keeping promotional card in position
    const allItems = Array.from(this.productGrid.children);
    const promotionalCardIndex = allItems.findIndex(item => 
      item.classList.contains('promotional-card-item')
    );
    
    allItems.forEach(item => item.remove());
    
    const middlePosition = Math.floor(visibleItems.length / 2);
    
    visibleItems.forEach((item, index) => {
      if (this.promotionalCard && index === middlePosition && promotionalCardIndex !== -1) {
        this.productGrid.appendChild(this.promotionalCard);
      }
      this.productGrid.appendChild(item);
    });
    
    if (this.promotionalCard && promotionalCardIndex !== -1 && !this.productGrid.contains(this.promotionalCard)) {
      this.productGrid.appendChild(this.promotionalCard);
    }
  }

  /**
   * ACTIVE FILTERS DISPLAY
   */
  initActiveFiltersDisplay() {
    const clearAllFiltersBtn = document.getElementById('clear-all-filters');
    if (clearAllFiltersBtn) {
      clearAllFiltersBtn.addEventListener('click', () => this.clearAllFilters());
    }
  }

  updateActiveFilters() {
    const activeFiltersContainer = document.getElementById('active-filters');
    const activeFiltersList = document.getElementById('active-filters-list');
    
    if (!activeFiltersContainer || !activeFiltersList) return;
    
    activeFiltersList.innerHTML = '';
    let hasActiveFilters = false;
    
    // Sort filter
    if (this.activeFilters.sort && this.activeFilters.sort !== 'manual') {
      hasActiveFilters = true;
      const sortLabels = {
        'created-descending': 'Newest',
        'price-ascending': 'Price: Low to High',
        'price-descending': 'Price: High to Low'
      };
      const chip = this.createFilterChip('Sort', sortLabels[this.activeFilters.sort], 'sort', this.activeFilters.sort);
      activeFiltersList.appendChild(chip);
    }
    
    // Product types (exclude hidden filters)
    this.activeFilters.productTypes.forEach(type => {
      // Don't show product type filters when on the specific product type collection page
      const currentPath = window.location.pathname;
      const productTypeCollections = ['/collections/hoodies', '/collections/tanks', '/collections/tees'];
      const isOnProductTypeCollection = productTypeCollections.includes(currentPath);
      const currentProductType = currentPath.split('/collections/')[1];
      
      // Skip showing the filter if we're on that product type's collection page
      if (isOnProductTypeCollection && type === currentProductType) {
        return; // Don't show "tees" filter when on /collections/tees
      }
      
      hasActiveFilters = true;
      const chip = this.createFilterChip('', type.charAt(0).toUpperCase() + type.slice(1), 'productType', type);
      activeFiltersList.appendChild(chip);
    });
    
    // Sizes
    this.activeFilters.sizes.forEach(size => {
      hasActiveFilters = true;
      const chip = this.createFilterChip('Size', size.toUpperCase(), 'size', size);
      activeFiltersList.appendChild(chip);
    });
    
    // Gender
    if (this.activeFilters.gender) {
      hasActiveFilters = true;
      const chip = this.createFilterChip('Gender', this.activeFilters.gender.charAt(0).toUpperCase() + this.activeFilters.gender.slice(1), 'gender', this.activeFilters.gender);
      activeFiltersList.appendChild(chip);
    }
    
    // Colors
    this.activeFilters.colors.forEach(color => {
      hasActiveFilters = true;
      const chip = this.createFilterChip('Color', color.charAt(0).toUpperCase() + color.slice(1), 'color', color);
      activeFiltersList.appendChild(chip);
    });
    
    // Mafia Tag
    if (this.activeFilters.mafiaTag) {
      hasActiveFilters = true;
      const mafiaLabels = {
        'lake-cumberland-mafia': 'Lake Cumberland Mafia',
        'norris-lake-mafia': 'Norris Lake Mafia',
        'dale-hollow-mafia': 'Dale Hollow Mafia',
        'custom-lake-apparel': 'Custom Lake Apparel'
      };
      const label = mafiaLabels[this.activeFilters.mafiaTag] || this.activeFilters.mafiaTag;
      const chip = this.createFilterChip('Mafia', label, 'mafiaTag', this.activeFilters.mafiaTag);
      activeFiltersList.appendChild(chip);
    }
    
    activeFiltersContainer.style.display = hasActiveFilters ? 'block' : 'none';
  }

  createFilterChip(category, value, filterType, filterValue) {
    const chip = document.createElement('div');
    chip.className = 'active-filter-chip';
    
    const displayText = category ? `${category}: ${value}` : value;
    
    chip.innerHTML = `
      ${displayText}
      <button class="remove-filter" data-filter-type="${filterType}" data-filter-value="${filterValue}">×</button>
    `;
    
    const removeBtn = chip.querySelector('.remove-filter');
    removeBtn.addEventListener('click', () => this.removeFilter(filterType, filterValue));
    
    return chip;
  }

  removeFilter(filterType, filterValue) {
    switch(filterType) {
      case 'sort':
        this.activeFilters.sort = 'manual';
        document.querySelectorAll('input[name="sort"], input[name="mobile-sort"]').forEach(radio => {
          radio.checked = radio.value === 'manual';
        });
        break;
      case 'productType':
        this.activeFilters.productTypes = this.activeFilters.productTypes.filter(type => type !== filterValue);
        document.querySelectorAll(`[data-product-type="${filterValue}"]`).forEach(btn => {
          btn.classList.remove('selected');
        });
        break;
      case 'size':
        this.activeFilters.sizes = this.activeFilters.sizes.filter(size => size !== filterValue);
        document.querySelectorAll(`[data-size="${filterValue}"]`).forEach(option => {
          option.classList.remove('selected');
        });
        break;
      case 'gender':
        this.activeFilters.gender = '';
        document.querySelectorAll(`[data-gender="${filterValue}"]`).forEach(option => {
          option.classList.remove('selected');
        });
        break;
      case 'color':
        this.activeFilters.colors = this.activeFilters.colors.filter(color => color !== filterValue);
        document.querySelectorAll(`[data-color="${filterValue}"]`).forEach(option => {
          option.classList.remove('selected');
        });
        break;
      case 'mafiaTag':
        this.activeFilters.mafiaTag = '';
        // Reset dropdown values
        const desktopMafiaSelect = document.getElementById('desktop-mafia-select');
        const mobileMafiaSelect = document.getElementById('mobile-mafia-select');
        if (desktopMafiaSelect) desktopMafiaSelect.value = '';
        if (mobileMafiaSelect) mobileMafiaSelect.value = '';
        break;
    }
    this.applyFilters();
  }

  clearAllFilters() {
    // Reset all filter states, but preserve hidden product type filter
    const hiddenProductTypes = this.hiddenProductTypeFilter ? [this.hiddenProductTypeFilter] : [];
    
    this.activeFilters = {
      sort: 'manual',
      productTypes: hiddenProductTypes, // Keep hidden filter active
      sizes: [],
      gender: '',
      colors: [],
      mafiaTag: ''
    };

    // Reset sort radio buttons
    document.querySelectorAll('input[name="sort"]').forEach(radio => {
      radio.checked = radio.value === 'manual';
    });
    document.querySelectorAll('input[name="mobile-sort"]').forEach(radio => {
      radio.checked = radio.value === 'manual';
    });

    // Reset all filter selections
    document.querySelectorAll('.product-type-option.selected').forEach(el => el.classList.remove('selected'));
    document.querySelectorAll('.size-option.selected').forEach(el => el.classList.remove('selected'));
    document.querySelectorAll('.gender-option.selected').forEach(el => el.classList.remove('selected'));
    document.querySelectorAll('.color-option.selected').forEach(el => el.classList.remove('selected'));

    // Reset mafia tag dropdowns
    const desktopMafiaSelect = document.getElementById('desktop-mafia-select');
    const mobileMafiaSelect = document.getElementById('mobile-mafia-select');
    if (desktopMafiaSelect) desktopMafiaSelect.value = '';
    if (mobileMafiaSelect) mobileMafiaSelect.value = '';

    this.applyFilters();
  }

  /**
   * UTILITY FUNCTIONS
   */
  showLoading() {
    // For instant filtering, we don't need loading animation
    // Keep function for compatibility but make it a no-op
  }

  hideLoading() {
    // Ensure no loading overlays are active
    const allLoadingOverlays = document.querySelectorAll('.loading-overlay.active');
    allLoadingOverlays.forEach(overlay => {
      overlay.classList.remove('active');
    });
  }

  /**
   * Ensure loading overlay is properly hidden on initialization
   */
  ensureLoadingOverlayHidden() {
    try {
      // Hide any active loading overlays immediately
      const allLoadingOverlays = document.querySelectorAll('.loading-overlay');
      allLoadingOverlays.forEach(overlay => {
        overlay.classList.remove('active');
      });
    } catch (error) {
      console.error('Error ensuring loading overlay is hidden:', error);
    }
  }

  /**
   * Force clear any stuck loading overlays (emergency function)
   */
  forceHideAllLoadingOverlays() {
    try {
      const allLoadingOverlays = document.querySelectorAll('.loading-overlay');
      allLoadingOverlays.forEach(overlay => {
        overlay.classList.remove('active');
        overlay.style.display = 'none';
      });
    } catch (error) {
      console.error('Error force hiding loading overlays:', error);
    }
  }

  updateProductCount(count) {
    if (this.productCount) {
      const total = this.productItems.length;
      this.productCount.textContent = count === total 
        ? `${count} product${count !== 1 ? 's' : ''}`
        : `${count} of ${total} product${total !== 1 ? 's' : ''}`;
    }
  }

  /**
   * QUICK ADD CART BUTTON FUNCTIONALITY
   */
  initQuickAddButtons() {
    // Add quick add button to all product cards
    const productCards = document.querySelectorAll('.card-product-add');
    
    productCards.forEach(card => {
      // Get product information
      const productLink = card.querySelector('a');
      if (!productLink) return;
      
      const productUrl = productLink.getAttribute('href');
      const productId = productUrl.split('/products/')[1]?.split('?')[0] || '';
      
      // Create and add the quick add button
      const quickAddButton = document.createElement('div');
      quickAddButton.className = 'quick-add-cart-button';
      quickAddButton.setAttribute('data-product-handle', productId);
      quickAddButton.innerHTML = `
        <svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M13.75 5.51002C13.75 3.31667 11.8655 1.55987 9.63239 1.77652L9.62967 1.77677C7.80822 1.94678 6.24997 3.79246 6.24997 5.70002V6.67002C6.24997 7.08423 5.91419 7.42002 5.49997 7.42002C5.08576 7.42002 4.74997 7.08423 4.74997 6.67002V5.70002C4.74997 3.10802 6.81103 0.534136 9.48891 0.283393C12.6152 -0.0191154 15.25 2.44382 15.25 5.51002V6.89002C15.25 7.30423 14.9142 7.64002 14.5 7.64002C14.0858 7.64002 13.75 7.30423 13.75 6.89002V5.51002Z" fill="#000000"></path>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M1.37351 7.81725C2.25892 6.76459 3.77694 6.25002 5.99997 6.25002H14C16.223 6.25002 17.741 6.76459 18.6264 7.81725C19.5047 8.86142 19.5869 10.2337 19.4454 11.5125L19.4443 11.5231L18.695 17.5167C18.5844 18.5429 18.3255 19.667 17.4096 20.5037C16.4984 21.3361 15.0868 21.75 13 21.75H6.99997C4.91312 21.75 3.5015 21.3361 2.59036 20.5037C1.67441 19.6669 1.41551 18.5429 1.30496 17.5166L0.554449 11.5125C0.412945 10.2337 0.495239 8.86142 1.37351 7.81725ZM2.04485 11.3424L2.79582 17.3501C2.89567 18.282 3.10252 18.9399 3.60209 19.3963C4.10844 19.8589 5.06682 20.25 6.99997 20.25H13C14.9331 20.25 15.8915 19.8589 16.3979 19.3963C16.8974 18.9399 17.1044 18.282 17.2042 17.3501L17.2057 17.337L17.9551 11.3422C18.0826 10.1837 17.9495 9.34277 17.4785 8.78279C17.0139 8.23045 16.0469 7.75002 14 7.75002H5.99997C3.95301 7.75002 2.98602 8.23045 2.52143 8.78279C2.05041 9.34279 1.91732 10.1838 2.04485 11.3424Z" fill="#000000"></path>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M12.4955 11C12.4955 10.4477 12.9432 10 13.4955 10H13.5044C14.0567 10 14.5044 10.4477 14.5044 11C14.5044 11.5523 14.0567 12 13.5044 12H13.4955C12.9432 12 12.4955 11.5523 12.4955 11Z" fill="#000000"></path>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.49448 11C5.49448 10.4477 5.9422 10 6.49448 10H6.50346C7.05575 10 7.50346 10.4477 7.50346 11C7.50346 11.5523 7.05575 12 6.50346 12H6.49448C5.9422 12 5.49448 11.5523 5.49448 11Z" fill="#000000"></path>
        </svg>
      `;
      
      card.appendChild(quickAddButton);
    });
    
    // Store reference to this
    const self = this;
    
    // Add event listener for all quick add buttons
    document.addEventListener('click', function(event) {
      if (event.target.closest('.quick-add-cart-button')) {
        event.preventDefault();
        event.stopPropagation();
        
        const button = event.target.closest('.quick-add-cart-button');
        const productHandle = button.dataset.productHandle;
        
        // First get the product variant ID
        fetch(`/products/${productHandle}.js`)
          .then(response => response.json())
          .then(product => {
            // Choose first available variant or default variant
            const variantId = product.variants[0].id;
            self.addToCart(variantId, 1);
          })
          .catch(error => {
            console.error('Error fetching product info:', error);
          });
      }
    });
  }

  addToCart(variantId, quantity) {
    const formData = {
      'items': [{
        'id': variantId,
        'quantity': quantity
      }]
    };
    
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      // Optionally refresh mini cart or show notification
      if (window.refreshCart) {
        window.refreshCart();
      } else {
        window.location.reload(); // Fallback if no cart refresh function
      }
    })
    .catch(error => {
      console.error('Error adding item to cart:', error);
    });
  }

  /**
   * COLLECTION TOGGLE INITIALIZATION
   * Ensures one of the two main collections is always active
   */
  initCollectionToggle() {
    const collectionToggle = document.getElementById('collection-toggle');
    if (!collectionToggle) return;

    const toggleOptions = collectionToggle.querySelectorAll('.collection-toggle__option');
    
    // Ensure at least one toggle is always active
    this.ensureActiveToggle(toggleOptions);
    
    // Add click event listeners to toggle options
    toggleOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        this.handleCollectionToggleClick(e, toggleOptions);
      });
    });
  }

  /**
   * Ensure at least one collection toggle is active
   */
  ensureActiveToggle(toggleOptions) {
    const hasActiveToggle = Array.from(toggleOptions).some(option => 
      option.classList.contains('active')
    );

    // If no toggle is active, activate the first one (Lake Life Mafia)
    if (!hasActiveToggle && toggleOptions.length > 0) {
      toggleOptions[0].classList.add('active');
    }
  }

  /**
   * Handle collection toggle click
   */
  handleCollectionToggleClick(e, toggleOptions) {
    const clickedOption = e.target;
    
    // If the clicked option is already active, don't allow deactivation
    if (clickedOption.classList.contains('active')) {
      e.preventDefault();
      return false;
    }
    
    // Remove active class from all options
    toggleOptions.forEach(option => option.classList.remove('active'));
    
    // Add active class to clicked option
    clickedOption.classList.add('active');
    
    // Show/hide the "Choose your mafia" filter based on active collection
    this.toggleMafiaFilter(clickedOption);
    
    // Navigate to the selected collection
    // The href will handle the navigation naturally
  }

  /**
   * Toggle visibility of the "Choose your mafia" filter
   * Only show when Lake Specific Collection (collection_2) is active
   */
  toggleMafiaFilter(activeOption) {
    const desktopMafiaFilter = document.getElementById('desktop-choose-mafia-filter');
    const mobileMafiaFilter = document.getElementById('mobile-choose-mafia-filter');
    const desktopMafiaSelect = document.getElementById('desktop-mafia-select');
    const mobileMafiaSelect = document.getElementById('mobile-mafia-select');
    
    // Get the href to determine which collection is active
    const activeHref = activeOption.getAttribute('href');
    
    // Check if this is the Lake Specific Collection (collection_2)
    // We can determine this by checking if it's the second option in the toggle
    const toggleOptions = activeOption.parentElement.querySelectorAll('.collection-toggle__option');
    const isLakeSpecificActive = Array.from(toggleOptions).indexOf(activeOption) === 1;
    
    if (desktopMafiaFilter) {
      desktopMafiaFilter.style.display = isLakeSpecificActive ? 'block' : 'none';
    }
    
    if (mobileMafiaFilter) {
      mobileMafiaFilter.style.display = isLakeSpecificActive ? 'block' : 'none';
    }
    
    // Reset mafia tag filter when hiding the filter
    if (!isLakeSpecificActive) {
      this.activeFilters.mafiaTag = '';
      if (desktopMafiaSelect) desktopMafiaSelect.value = '';
      if (mobileMafiaSelect) mobileMafiaSelect.value = '';
      this.applyFilters();
    }
  }

  handleMafiaTagFilter(value) {
    this.activeFilters.mafiaTag = value;
    this.applyFilters();
  }

  /**
   * Handle pagination visibility based on filtered results
   */
  handlePaginationVisibility(visibleCount) {
    const pagination = document.querySelector('.pagination');
    
    if (!pagination) return;
    
    // Check if any filters are active
    const hasActiveFilters = this.activeFilters.productTypes.length > 0 ||
                           this.activeFilters.sizes.length > 0 ||
                           this.activeFilters.gender ||
                           this.activeFilters.colors.length > 0 ||
                           this.activeFilters.mafiaTag ||
                           (this.activeFilters.sort && this.activeFilters.sort !== 'manual');
    
    // Hide pagination only when filters are actively being used
    if (hasActiveFilters) {
      pagination.style.display = 'none';
    } else {
      pagination.style.display = '';
    }
  }

  /**
   * Get the currently active collection from the toggle
   */
  /**
   * Whether the Lake Life / Lake Specific collection toggle is enabled.
   * When the merchant turns the toggle off in the theme editor, the
   * #collection-toggle element is not rendered, so the Lake Life Mafia
   * membership filtering on product-type collections must be skipped
   * (otherwise every product gets hidden -> "No products found").
   */
  isCollectionToggleEnabled() {
    return !!document.getElementById('collection-toggle');
  }

  getActiveCollection() {
    const collectionToggle = document.getElementById('collection-toggle');
    if (!collectionToggle) return null;
    
    const activeOption = collectionToggle.querySelector('.collection-toggle__option.active');
    if (!activeOption) return null;
    
    // Get the href attribute which contains the collection URL
    const href = activeOption.getAttribute('href');
    if (!href) return null;
    
    // Extract collection handle from URL (e.g., /collections/lake-life-mafia -> lake-life-mafia)
    const matches = href.match(/\/collections\/([^\/]+)/);
    return matches ? matches[1] : null;
  }

  /**
   * Clear/deactivate the collection toggle
   */
  clearCollectionToggle() {
    const collectionToggle = document.getElementById('collection-toggle');
    if (!collectionToggle) return;
    
    const toggleOptions = collectionToggle.querySelectorAll('.collection-toggle__option');
    
    // Remove active class from all toggle options
    toggleOptions.forEach(option => option.classList.remove('active'));
    
    // Hide the mafia filter since no collection is selected
    const desktopMafiaFilter = document.getElementById('desktop-choose-mafia-filter');
    const mobileMafiaFilter = document.getElementById('mobile-choose-mafia-filter');
    
    if (desktopMafiaFilter) desktopMafiaFilter.style.display = 'none';
    if (mobileMafiaFilter) mobileMafiaFilter.style.display = 'none';
  }
}

// iOS-specific DOM ready handling
function initializeCollectionFilters() {
  // Prevent duplicate initialization
  if (window.collectionFiltersInitialized) {
    return;
  }
  window.collectionFiltersInitialized = true;

  const collectionFilters = new CollectionFilters();
  
  // Store instance globally for mobile compatibility
  window.collectionFiltersInstance = collectionFilters;
  
  // Provide a global function to manually clear stuck overlays if needed
  window.clearStuckLoadingOverlays = () => {
    collectionFilters.forceHideAllLoadingOverlays();
  };

  // iOS-specific: Force immediate display after initialization
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) {
    setTimeout(() => {
      // Emergency fix for white screen - ensure all products are visible
      const productItems = document.querySelectorAll('.product-item');
      productItems.forEach(item => {
        if (item.style.display === 'none' && !item.hasAttribute('data-filtered')) {
          item.style.display = '';
        }
      });

      // Hide any stuck loading overlays
      const loadingOverlays = document.querySelectorAll('.loading-overlay');
      loadingOverlays.forEach(overlay => {
        overlay.style.display = 'none !important';
        overlay.classList.remove('active');
      });
      
      // Force grid refresh
      const productGrid = document.getElementById('product-grid');
      if (productGrid) {
        productGrid.style.opacity = '1';
        productGrid.style.visibility = 'visible';
      }
    }, 100);
  }
  
  // Additional mobile compatibility check for all devices
  setTimeout(() => {
    const currentPath = window.location.pathname;
    const productTypeCollections = ['/collections/hoodies', '/collections/tanks', '/collections/tees'];
    const isOnProductTypeCollection = productTypeCollections.includes(currentPath);
    
    // Check for gender parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const genderParam = urlParams.get('gender');
    
    if (genderParam) {
      const validGenders = ['men', 'women'];
      const gender = genderParam.toLowerCase();
      
      if (validGenders.includes(gender)) {
        // Apply gender filter immediately
        collectionFilters.activeFilters.gender = gender;
        collectionFilters.activateGenderButton(gender);
        collectionFilters.applyFiltersImmediate();
      }
    }
    
    if (isOnProductTypeCollection) {
      // Force mobile compatibility check
      collectionFilters.ensureMobileCompatibility();
    }
  }, 500);
}

// Initialize when DOM is loaded - with iOS fallback
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCollectionFilters);
} else {
  // DOM already loaded
  initializeCollectionFilters();
}

// iOS Safari specific - additional initialization check
window.addEventListener('load', () => {
  if (!window.collectionFiltersInitialized) {
    initializeCollectionFilters();
  }
  
  // Additional mobile compatibility check after full page load
  setTimeout(() => {
    const currentPath = window.location.pathname;
    const productTypeCollections = ['/collections/hoodies', '/collections/tanks', '/collections/tees'];
    const isOnProductTypeCollection = productTypeCollections.includes(currentPath);
    
    // Check for gender parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const genderParam = urlParams.get('gender');
    
    if (genderParam && window.collectionFiltersInstance) {
      const validGenders = ['men', 'women'];
      const gender = genderParam.toLowerCase();
      
      if (validGenders.includes(gender)) {
        // Apply gender filter immediately
        window.collectionFiltersInstance.activeFilters.gender = gender;
        window.collectionFiltersInstance.activateGenderButton(gender);
        window.collectionFiltersInstance.applyFiltersImmediate();
      }
    }
    
    if (isOnProductTypeCollection && window.collectionFiltersInstance) {
      window.collectionFiltersInstance.ensureMobileCompatibility();
    }
  }, 1000);
});

// Store instance globally for mobile compatibility
window.addEventListener('DOMContentLoaded', () => {
  if (window.collectionFiltersInstance) {
    // Additional mobile check
    setTimeout(() => {
      const currentPath = window.location.pathname;
      const productTypeCollections = ['/collections/hoodies', '/collections/tanks', '/collections/tees'];
      const isOnProductTypeCollection = productTypeCollections.includes(currentPath);
      
      // Check for gender parameter in URL
      const urlParams = new URLSearchParams(window.location.search);
      const genderParam = urlParams.get('gender');
      
      if (genderParam) {
        const validGenders = ['men', 'women'];
        const gender = genderParam.toLowerCase();
        
        if (validGenders.includes(gender)) {
          // Apply gender filter immediately
          window.collectionFiltersInstance.activeFilters.gender = gender;
          window.collectionFiltersInstance.activateGenderButton(gender);
          window.collectionFiltersInstance.applyFiltersImmediate();
        }
      }
      
      if (isOnProductTypeCollection) {
        window.collectionFiltersInstance.ensureMobileCompatibility();
      }
    }, 800);
  }
}); 